import { Link, Outlet, createFileRoute, useLocation } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiFile,
  FiHeadphones,
  FiImage,
  FiLogOut,
  FiVideo,
  FiVolume2,
} from "react-icons/fi";
import { storage } from "../lib/storage";
import { useAppDispatch } from "../store";
import { logout } from "../store/slices/authSlice";
import { setMediaType } from "../store/slices/mediaSlice";
import type { MediaType } from "../store/slices/mediaSlice";

export const Route = createFileRoute("/media")({
  component: MediaLayout,
});

// navigation item type includes both MediaType and phonics (which is separate)
// because phonics is a distinct type, not using the union type from mediaType
type NavItemType = MediaType | "phonics";

interface NavItem {
  type: NavItemType;
  icon: typeof FiHeadphones;
  label: string;
  path: string;
}

function MediaLayout() {
  const dispatch = useAppDispatch();
  const location = useLocation();

  // Sidebar collapsed state - persist in localStorage
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebar-collapsed");
    return saved ? JSON.parse(saved) : false;
  });

  const navItems: NavItem[] = [
    { type: "audio", icon: FiHeadphones, label: "Audio", path: "/media/audio" },
    { type: "image", icon: FiImage, label: "Images", path: "/media/images" },
    { type: "video", icon: FiVideo, label: "Videos", path: "/media/videos" },
    { type: "file", icon: FiFile, label: "Others", path: "/media/files" },
    { type: "phonics", icon: FiVolume2, label: "Phonics", path: "/media/phonics" },
  ];

  // Determine active item based on current pathname
  const activeType =
    navItems.find((item) => location.pathname.startsWith(item.path))?.type || "audio";

  // Sync Redux state with current route (for browser back/forward)
  useEffect(() => {
    // Only dispatch for regular media types, not phonics
    if (activeType !== "phonics") {
      dispatch(setMediaType(activeType as MediaType));
    }
  }, [activeType, dispatch]);

  // Persist sidebar collapsed state
  useEffect(() => {
    localStorage.setItem("sidebar-collapsed", JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const handleNavClick = (type: NavItemType) => {
    // only dispatch for regular media types, not phonics
    if (type !== "phonics") {
      dispatch(setMediaType(type));
    }
  };

  const handleLogout = () => {
    storage.clearTokens();
    dispatch(logout());
    window.location.href = "/";
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* <a href="#main-content" className="skip-to-main">
        Skip to main content
      </a> */}

      {/* Sidebar */}
      <aside
        className={`${
          sidebarCollapsed ? "w-16" : "w-64"
        } flex flex-col border-r border-gray-200 bg-white transition-all duration-300`}
      >
        {/* Sidebar Header */}
        <div className="flex h-16 items-center justify-between border-b border-gray-200 px-4">
          {!sidebarCollapsed && (
            <h1 className="text-lg font-semibold text-gray-900">Media Library</h1>
          )}
          <button
            type="button"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sidebarCollapsed ? <FiChevronRight size={20} /> : <FiChevronLeft size={20} />}
          </button>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 space-y-1 px-2 py-4" aria-label="Media types">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeType === item.type;

            return (
              <Link
                key={item.type}
                to={item.path}
                onClick={() => handleNavClick(item.type)}
                className={`${
                  isActive
                    ? "bg-blue-50 text-blue-600"
                    : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                } group flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors`}
                aria-current={isActive ? "page" : undefined}
                title={sidebarCollapsed ? item.label : undefined}
              >
                <Icon
                  className={`${isActive ? "text-blue-600" : "text-gray-400 group-hover:text-gray-500"} ${
                    sidebarCollapsed ? "mx-auto" : "mr-3"
                  } h-5 w-5 flex-shrink-0`}
                />
                {!sidebarCollapsed && <span>{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Logout Button */}
        <div className="border-t border-gray-200 p-2">
          <button
            type="button"
            onClick={handleLogout}
            className="group flex w-full items-center rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
            title={sidebarCollapsed ? "Log out" : undefined}
          >
            <FiLogOut
              className={`${
                sidebarCollapsed ? "mx-auto" : "mr-3"
              } h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500`}
            />
            {!sidebarCollapsed && <span>Log out</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main id="main-content" className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
